import { ReactNode, useEffect, useMemo, useState } from 'react';
import { Customer, InputRuleApiModel, Meta, SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import SidePanelSection, { SidePanelSections } from '../../../../components/sidepanel-section';
import { SettingsCache } from '~/lib/consts/settings-cache';
import { P2GBox, P2GText, P2GGrid, P2GDropdown, P2GButton } from '@parcel2go/core-ui';
import { IsWindsor } from '../../lib/helpers/IsWindsor';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { CustomerTaxDetails } from './CustomerTaxDetails';

interface props {
  itemMeta: Meta;
  onSave?: (sender: Customer, recipient: Customer, meta: Meta) => void;
  onUpdate?: (sender: Customer, recipient: Customer, meta: Meta) => void;
  marketplaceIds: string[];
  close?: ReactNode;
  sender: Customer;
  recipient: Customer;
  validation: {
    RecipientValidation: InputRuleApiModel[];
    SenderValidation: InputRuleApiModel[];
    ItemValidation: InputRuleApiModel[];
  };
  duties: ReactNode;
}

export const CustomsSelection: React.FC<props> = (props) => {
  // Item.Meta
  const [reasonForExport, setReasonForExport] = useState(props.itemMeta['ExportReason']?.Value || 'Sale');
  const [sender, setSender] = useState({ ...props.sender });
  const [recipient, setRecipient] = useState({ ...props.recipient });
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  const reasonsForExport = useMemo(() => {
    const profile = SettingsCache.profile?.toUpperCase() || '';
    if (profile.includes('EVRI.SME') || profile.includes('ASENDIA') || props.marketplaceIds.includes('ebay')) {
      return [{ value: 'Sale', text: 'Sale' }];
    }

    return [
      { value: 'Sale', text: 'Sale' },
      { value: 'Gift', text: 'Gift' },
      { value: 'Sample', text: 'Sample' },
      { value: 'Repair', text: 'Repair' },
      { value: 'Documents', text: 'Documents' },
      { value: 'IntraCompanyTransfer', text: 'Intra Company Transfer' },
      { value: 'TemporaryExport', text: 'Temporary Export' },
      { value: 'NotSold', text: 'Not Sold' },
      { value: 'PersonalEffects', text: 'Personal Effects' },
      { value: 'Return', text: 'Return of Goods' }
    ];
  }, [props.marketplaceIds]);

  useEffect(() => {
    if (props.onUpdate) {
      props.onUpdate(sender, recipient, {
        ExportReason: { Value: reasonForExport, Type: 'string' }
      });
    }
  }, [sender, recipient, reasonForExport]);

  const isWindsor = IsWindsor({ Sender: sender, Recipient: recipient } as SmartSendItemApiModel);

  return (
    <>
      <SidePanelSections>
        {props.validation.ItemValidation.find((x) => x.name === 'ExportReason') && (
          <SidePanelSection
            title={
              isWindsor
                ? t('ADDITIONAL_INFO_SUBTITLE', { defaultValue: 'Additional Information:' })
                : t('COMMERCIAL_INVOICE_SUBTITLE', { defaultValue: 'Commercial Invoice:' })
            }
          >
            <P2GBox mb={1}>
              <P2GText variant="body2">
                {isWindsor
                  ? t('WITH_WINDSOR_TEXT', {
                      defaultValue:
                        'As your package is going to be delivered to Northern Ireland we need some additional information.'
                    })
                  : t('WITHOUT_WINDSOR_TEXT', {
                      defaultValue:
                        'The following must be filled in to generate your customs documents. This information will minimise any  risk of delays or customs charges.'
                    })}
              </P2GText>
            </P2GBox>
            <P2GGrid container spacing={2}>
              <P2GGrid item xs={6}>
                <P2GDropdown
                  value={reasonForExport}
                  id="Reason For Export"
                  options={reasonsForExport}
                  onChange={(v: string) => setReasonForExport(v)}
                  required={!!props.validation.ItemValidation.find((x) => x.name === 'ExportReason' && x.required)}
                />
              </P2GGrid>
            </P2GGrid>
          </SidePanelSection>
        )}

        {props.duties}

        <CustomerTaxDetails
          wrapper={(x) => <>{x}</>}
          customer={sender}
          onUpdate={setSender}
          includeTitle
          validation={props.validation.SenderValidation}
          type="Sender"
        />

        <CustomerTaxDetails
          wrapper={(x) => <>{x}</>}
          customer={recipient}
          onUpdate={setRecipient}
          includeTitle
          validation={props.validation.RecipientValidation}
          type="Recipient"
        />
      </SidePanelSections>
      {props.onSave && (
        <P2GBox textAlign="center" mt={2}>
          <P2GButton
            onClick={() =>
              props.onSave(sender, recipient, {
                ExportReason: { Value: reasonForExport, Type: 'string' }
              })
            }
          >
            {t('SAVE_BUTTON', { defaultValue: 'Save' })}
          </P2GButton>
        </P2GBox>
      )}
    </>
  );
};
